import React from 'react';
import posthog from 'posthog-js';

type Props = { children: React.ReactNode };
type State = { hasError: boolean };

/**
 * Catches render-time crashes so a single bad API payload degrades to a
 * recoverable message instead of white-screening the whole CRM.
 *
 * Errors are forwarded to PostHog explicitly — `posthog.init` only auto-captures
 * window.onerror, which React swallows for errors thrown during render.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    posthog.capture('$exception', {
      $exception_message: error.message,
      $exception_type: error.name,
      $exception_stack_trace_raw: error.stack,
      component_stack: errorInfo.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-4">
          <img src="/tini-logo.png" alt="Tini Express" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <p className="text-gray-700 mb-4">
            This page hit an unexpected error. The team has been notified.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
